import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { BrowserModule } from '@angular/platform-browser';
import {
  LOCALE_ID,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AuxiliaresModule } from './auxiliares/auxiliares.module';
import { OVERLAY_DEFAULT_CONFIG, OverlayContainer } from '@angular/cdk/overlay';
import { AppOverlayContainer } from './app-overlay-container';
import { RouteReuseStrategy, TitleStrategy } from '@angular/router';
import { ReusoConParametros } from './auxiliares/reuso-rutas';
import { TituloPagina } from './auxiliares/titulo-pagina';
import { OrigenService } from './auxiliares/servicios/origen.service';

// Locale

import {
  HashLocationStrategy,
  LocationStrategy,
  registerLocaleData,
} from '@angular/common';
import localeEs from '@angular/common/locales/es-AR';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ThemesService } from './auxiliares/servicios/themes.service';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { LoadingInterceptor } from './auxiliares/interceptors/loading.interceptor';
import { ServiceWorkerModule } from '@angular/service-worker';
import { API, VERSION, environment } from '../environments/environment';
import { PwaService } from './auxiliares/servicios/pwa.service';
import { HighchartsChartModule } from 'highcharts-angular';
import { MarkdownModule } from 'ngx-markdown';
import { EnvService } from './env';
import { SnowOverlayComponent } from './componentes/snow-overlay/snow-overlay.component';
import { TemaOverlayComponent } from './componentes/tema-overlay/tema-overlay.component';
// es-AR con fechas dd/MM/yyyy y horas de 24 h: así `date:'short'`, `date:'medium'`
// y el formato por defecto del DatePipe nunca muestran a. m./p. m. ni años de dos cifras.
const localeEsAr24h = [...localeEs] as typeof localeEs;
localeEsAr24h[10] = [
  'dd/MM/yyyy',
  'dd/MM/yyyy',
  "d 'de' MMMM 'de' y",
  "EEEE, d 'de' MMMM 'de' y",
];
localeEsAr24h[11] = ['HH:mm', 'HH:mm:ss', 'HH:mm:ss z', 'HH:mm:ss zzzz'];
localeEsAr24h[12] = ['{1} {0}', '{1} {0}', '{1} {0}', '{1} {0}'];
registerLocaleData(localeEsAr24h, 'es-AR');

export function loadEnv(envService: EnvService): () => Promise<void> {
  return () => envService.loadEnvironment();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HighchartsChartModule,
    MarkdownModule.forRoot(),
    //
    AuxiliaresModule,
    SnowOverlayComponent,
    TemaOverlayComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideAnimationsAsync(),
    ThemesService,
    provideAppInitializer(() => {
      const initializerFn = (
        (themeService: ThemesService) => () =>
          themeService.init()
      )(inject(ThemesService));
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'es-AR' },
    // Campos con contorno en toda la app (formularios unificados)
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      // subscriptSizing dynamic: sin reservar 22 px bajo cada campo; el
      // mensaje de error aparece sólo cuando hace falta
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    { provide: OverlayContainer, useClass: AppOverlayContainer },
    // Rutas que se recrean si cambian sus filtros de la URL (data.recrearCon)
    { provide: RouteReuseStrategy, useClass: ReusoConParametros },
    // "Luminarias · IRIX" en la pestaña, con el title de la ruta
    { provide: TitleStrategy, useClass: TituloPagina },
    // De dónde vino cada página: migas y flecha de volver
    provideAppInitializer(() => inject(OrigenService).iniciar()),
    // Una sola tipografía de íconos: <mat-icon> usa Material Symbols (el
    // relleno lo da styles.scss). Ver docs/ICONOS.md.
    provideAppInitializer(() => {
      inject(MatIconRegistry).setDefaultFontSetClass(
        'material-symbols-outlined',
        'mat-ligature-font',
      );
    }),
    provideAppInitializer(() => {
      const envService = inject(EnvService);
      return loadEnv(envService)();
    }),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  pwa = inject(PwaService);

  constructor() {
    console.log(`Versión: ${VERSION}`);
    console.log(`API: ${API}`);
  }
}
